import React, { useMemo, useRef, useState } from 'react';
import { Check, ChevronRight, Hourglass } from 'lucide-react';
import PageBack from '../ui/PageBack';
import type { CampaignPlanItem, CampaignView, PlanStatus } from '../data/selectors/campaignView';
import { decideCampaignPlanInDemo, type DecideCampaignPlan } from '../data/campaignDecision';
import { useDataClock, useDataMode } from '../data';
import { buildPipelineStages, type PipelineStageId } from '../lib/pipelineStages';
import type { FeedState } from '../data/selectors/feed';
import { incidentFromThreatEvent } from '../lib/incident';
import { campaignLabel, incidentLabel } from '../lib/ids';
import { prefersReducedMotion } from '../lib/motion';
import { sourcesLine } from '../lib/sourceNames';
import { formatAge, formatUtcDateTime } from '../lib/time';
import { useToast } from '../app/ToastProvider';
import { EntityChips, SEVERITY_CLASS, StateBadge } from '../components/c3/FeedBits';
import MemberAgent from '../components/c3/MemberAgent';
import PipelineFlow from '../components/c3/PipelineFlow';
import EmptyState from '../ui/EmptyState';

interface CampaignDetailProps {
  view: CampaignView;
  onBack: () => void;
  /** How a campaign decision is sent. Demo's simulation of the documented contract when not given (BACKEND.md, item 84). */
  decide?: DecideCampaignPlan;
}

const PLAN_STATUS_LABEL: Record<PlanStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  reverted: 'Reverted',
  none: 'No plan yet',
};

/** What a Demo approval makes of an incident that waited on a person (session only: the Demo data is not changed). */
const APPROVED_STATE: FeedState = { kind: 'pending_execution', label: 'Approved' };

const SHOWN_LABEL: Record<ShownStatus, string> = { ...PLAN_STATUS_LABEL, approved: 'Approved' };

/** The order the plan's counts are told in: what waits first, what is over last. */
const COUNT_ORDER: ShownStatus[] = ['pending', 'confirmed', 'approved', 'in_progress', 'completed', 'reverted', 'cancelled', 'none'];

/** The plan's statuses, and the one a Demo approval adds for this session only. */
type ShownStatus = PlanStatus | 'approved';

/**
 * Where a stage leads on this screen. Stages 1 to 5 end as the incidents of the campaign (the Overview too sends
 * most of its stages to one place); stages 6 and 7 end as its plan.
 */
type StageTarget = 'incidents' | 'plan';

const STAGE_TARGET: Record<PipelineStageId, StageTarget> = {
  ingest: 'incidents',
  dedup: 'incidents',
  score: 'incidents',
  classify: 'incidents',
  consolidate: 'incidents',
  plan: 'plan',
  execute: 'plan',
};

/**
 * One campaign: what it is, the seven stages of the pipeline counted over it, the incidents it joins, and its
 * remediation plan (the plans of its incidents, as one). A lone incident opens here as a campaign of one.
 * Replaces the incident screen: a plan and its execution belong to the campaign, not to each incident.
 */
const CampaignDetail: React.FC<CampaignDetailProps> = ({ view, onBack, decide }) => {
  const { row, plan, counters } = view;
  const { demo } = useDataMode();
  const { refreshNow } = useDataClock();
  const { notify } = useToast();
  // The incidents the person has approved in this session, and a request on its way.
  const [decided, setDecided] = useState<ReadonlySet<string>>(() => new Set());
  const [deciding, setDeciding] = useState(false);
  const incidentsHeading = useRef<HTMLHeadingElement>(null);
  const planHeading = useRef<HTMLHeadingElement>(null);
  // The panel a stage has just taken the reader to; its heading wears a ring until focus leaves it.
  const [arrived, setArrived] = useState<StageTarget | null>(null);
  const nowMs = Date.now();

  const idLabel = view.kind === 'campaign' ? campaignLabel(row.id) : incidentLabel(row.id);
  const incidents = row.members.length;
  const waiting = plan.items.filter((item) => item.needsDecision && !decided.has(item.threatId));
  const awaiting = waiting.length;
  // After a Demo approval what waited reads as approved, in the counts and in its line, so the page agrees with itself.
  const counts = useMemo(() => {
    const out: Record<ShownStatus, number> = { ...plan.counts, approved: 0 };
    plan.items.forEach((item) => {
      if (!decided.has(item.threatId)) return;
      out[item.status] -= 1;
      out.approved += 1;
    });
    return out;
  }, [plan, decided]);
  // What a Demo approval changes must read the same everywhere on the page: the Plan stage, the incidents and the header.
  const stages = useMemo(() => {
    const moved = plan.items.filter((item) => decided.has(item.threatId) && item.status === 'pending').length;
    if (moved === 0) return buildPipelineStages(counters);
    const actions = { ...counters.actions, pending: counters.actions.pending - moved, confirmed: counters.actions.confirmed + moved };
    return buildPipelineStages({ ...counters, actions });
  }, [counters, plan, decided]);
  const stateOf = (id: string, state: FeedState): FeedState => (decided.has(id) ? APPROVED_STATE : state);
  const rowState: FeedState =
    decided.size === 0 || row.state.kind !== 'pending_approval'
      ? row.state
      : awaiting === 0
        ? APPROVED_STATE
        : { ...row.state, count: awaiting };
  const summary =
    decided.size === 0
      ? plan.summary
      : awaiting === 0
        ? 'Approved in this session'
        : `${awaiting} of ${plan.total} plan${plan.total === 1 ? '' : 's'} await${awaiting === 1 ? 's' : ''} your decision`;

  // A stage takes the reader to what it produced: the page scrolls there and focus follows, so a keyboard or a
  // screen reader lands on it too. It scrolls smoothly unless the person asked for less motion.
  const showStage = (id: PipelineStageId) => {
    const target = STAGE_TARGET[id];
    const heading = (target === 'plan' ? planHeading : incidentsHeading).current;
    if (!heading) return;
    heading.scrollIntoView?.({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    heading.focus({ preventScroll: true });
    setArrived(target);
  };

  // One decision for the campaign: the request lists what the person is looking at, and the answer says incident by
  // incident how it went. A failure does not undo the others; pressing the button again sends only what is left.
  const approve = async () => {
    if (deciding || awaiting === 0) return;
    const remediationIds = waiting.flatMap((item) => (item.plan ? [item.plan._id] : []));
    if (remediationIds.length === 0) {
      // Waiting on a person but with no plan record to decide: nothing to send, and it must not read as approved.
      notify({ tone: 'error', title: 'Could not approve the plan.', message: 'There is no plan to approve yet. Try again in a moment.' });
      return;
    }
    setDeciding(true);
    const send = decide ?? decideCampaignPlanInDemo(plan.items, decided);
    try {
      const response = await send({
        campaign_id: view.id,
        decision_id: `decision-${Date.now().toString(36)}`,
        decision: 'approve',
        remediation_ids: remediationIds,
      });
      const settled = response.results.filter((r) => r.outcome !== 'failed').map((r) => r.threat_event_id);
      setDecided((prev) => {
        const next = new Set(prev);
        settled.forEach((id) => next.add(id));
        return next;
      });
      const failed = response.results.filter((r) => r.outcome === 'failed');
      if (response.status === 'complete') {
        notify({
          tone: 'success',
          title: 'Plan approved (Demo).',
          message: 'The Demo data is not changed, so the feed still shows these as awaiting.',
        });
      } else {
        const names = failed.map((r) => incidentLabel(r.threat_event_id)).join(', ');
        const done = response.results.length - failed.length;
        notify({
          tone: 'error',
          title:
            response.status === 'failed'
              ? 'Could not approve the plan.'
              : `Approved for ${done} of ${response.results.length} incidents.`,
          message: `Could not approve ${names}. Nothing was changed for ${failed.length === 1 ? 'it' : 'them'}. Try again.`,
        });
      }
    } catch {
      notify({ tone: 'error', title: 'Could not approve the plan.', message: 'Nothing was changed. Try again in a moment.' });
    } finally {
      setDeciding(false);
    }
  };

  return (
    <section className="c3-band c3-camp" aria-labelledby="c3-camp-title">
      <div className="c3-camp-head">
        <PageBack onClick={onBack} />
        <div className="min-w-0 flex-1">
          <h1 id="c3-camp-title" className="c3-camp-title">
            {row.title}
          </h1>
          <div className="c3-camp-meta">
            <span className={`tf-sev ${SEVERITY_CLASS[row.severity]}`}>{row.severity}</span>
            <StateBadge state={rowState} icon={rowState === APPROVED_STATE ? Check : undefined} />
            <span className={`tf-incident-id ${view.kind === 'campaign' ? 'is-campaign' : 'is-threat'}`}>{idLabel}</span>
            <span className="c3-camp-facts">
              {[
                view.kind === 'campaign' ? `Campaign of ${incidents} incidents` : 'Campaign of one incident',
                row.firstEventMs != null ? `First event ${formatUtcDateTime(row.firstEventMs)}` : '',
                row.sources.length ? sourcesLine(row.sources) : '',
              ]
                .filter(Boolean)
                .map((fact, i) => (
                  // A real space between the facts, so a screen reader or a copy does not run them together.
                  <React.Fragment key={fact}>
                    {i > 0 ? ' ' : null}
                    <span>{fact}</span>
                  </React.Fragment>
                ))}
            </span>
          </div>
          {row.description ? <p className="c3-camp-desc">{row.description}</p> : null}
          {row.entities.length ? (
            <div className="c3-camp-entities">
              <span className="c3-camp-label">Affected entities</span>
              <EntityChips entities={row.entities} limit={8} />
            </div>
          ) : null}
        </div>
      </div>

      <h2>
        AI Analysis Pipeline{' '}
        <span className="meta">
          This campaign through the seven stages, over all time (the Overview shows the period you choose) · click a stage
          to open it
        </span>
      </h2>
      <PipelineFlow stages={stages} label="Campaign pipeline stages" onSelect={showStage} hideZeros />

      <div className="c3-camp-grid">
        <div className="c3-camp-panel">
          <h2
            ref={incidentsHeading}
            tabIndex={-1}
            className={arrived === 'incidents' ? 'is-arrived' : undefined}
            onBlur={() => setArrived(null)}
          >
            Incidents <span className="meta">{incidents} in this campaign</span>
          </h2>
          <ul className="c3-camp-list">
            {row.members.map((member) => (
              <li key={member.threat.event._id} className="c3-camp-incident">
                <div className="c3-camp-incident-top">
                  <span className={`tf-sev ${SEVERITY_CLASS[member.severity]}`}>{member.severity}</span>
                  <span className="c3-camp-incident-title">{member.title}</span>
                </div>
                <div className="c3-camp-incident-meta">
                  <span className="tf-incident-id is-threat">{incidentLabel(member.threat.event._id)}</span>
                  <StateBadge
                    state={stateOf(member.threat.event._id, member.state)}
                    icon={decided.has(member.threat.event._id) ? Check : undefined}
                  />
                  <span>{formatAge(member.firstEventMs, nowMs)}</span>
                  {member.sources.length ? <span>{sourcesLine(member.sources)}</span> : null}
                </div>
                {member.description ? <p className="c3-camp-incident-desc">{member.description}</p> : null}
                {member.entities.length ? <EntityChips entities={member.entities} /> : null}
              </li>
            ))}
          </ul>
        </div>

        <div className="c3-camp-panel">
          <h2
            ref={planHeading}
            tabIndex={-1}
            className={arrived === 'plan' ? 'is-arrived' : undefined}
            onBlur={() => setArrived(null)}
          >
            Remediation plan <span className="meta">one plan for the campaign</span>
          </h2>
          <PlanSummary
            summary={summary}
            plan={plan}
            counts={counts}
            awaiting={awaiting}
            deciding={deciding}
            canApprove={demo}
            onApprove={() => void approve()}
          />
          {plan.total === 0 ? (
            <EmptyState title="No remediation plan yet">
              The agent writes a plan for each incident of the campaign; they appear here as one plan.
            </EmptyState>
          ) : (
            <ul className="c3-camp-list">
              {plan.items.map((item) => (
                <PlanItem
                  key={item.threatId}
                  item={item}
                  live={!demo}
                  onDone={refreshNow}
                  view={view}
                  approved={decided.has(item.threatId)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};

const PlanSummary: React.FC<{
  summary: string;
  plan: CampaignView['plan'];
  counts: Record<ShownStatus, number>;
  awaiting: number;
  deciding: boolean;
  canApprove: boolean;
  onApprove: () => void;
}> = ({ summary, plan, counts, awaiting, deciding, canApprove, onApprove }) => (
  <div className="c3-camp-plan-head">
    {awaiting > 0 ? (
      <p className="c3-camp-plan-summary is-need">
        <Hourglass size={14} strokeWidth={1.5} aria-hidden />
        {summary}
      </p>
    ) : null}
    {plan.total > 0 ? (
      <ul className="c3-camp-counts" aria-label="Incidents by plan status">
        {COUNT_ORDER.filter((status) => counts[status] > 0).map((status) => (
          <li key={status} className="c3-camp-count">
            <b>{counts[status]}</b> {SHOWN_LABEL[status].toLowerCase()}
          </li>
        ))}
      </ul>
    ) : null}
    {canApprove && plan.awaiting > 0 ? (
      <div className="c3-camp-decide">
        <button type="button" className={`c3-btn c3-btn-approve${awaiting === 0 ? ' is-done' : ''}`} disabled={awaiting === 0 || deciding} onClick={onApprove}>
          {awaiting === 0 ? <Check size={14} strokeWidth={1.5} aria-hidden /> : null}
          {awaiting === 0 ? 'Approved' : deciding ? 'Approving…' : `Approve plan for ${awaiting} incident${awaiting === 1 ? '' : 's'}`}
        </button>
        <span className="c3-camp-note">Demo: the approval is not saved.</span>
      </div>
    ) : null}
  </div>
);

const PlanItem: React.FC<{
  item: CampaignPlanItem;
  live: boolean;
  onDone: () => void;
  view: CampaignView;
  /** A Demo approval of this session covers it. */
  approved: boolean;
}> = ({ item, live, onDone, view, approved }) => {
  const [open, setOpen] = useState(false);
  const member = view.row.members.find((m) => m.threat.event._id === item.threatId);
  return (
    <li className="c3-camp-plan-item">
      <details onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
        <summary>
          <ChevronRight className="c3-camp-chev" size={14} strokeWidth={1.5} aria-hidden />
          <span className="c3-camp-incident-title">{item.title}</span>
          <span className="tf-incident-id is-threat">{incidentLabel(item.threatId)}</span>
          <span className="c3-camp-plan-status">{approved ? SHOWN_LABEL.approved : PLAN_STATUS_LABEL[item.status]}</span>
          {item.mode ? <span className="tf-state-tag">{item.mode === 'auto' ? 'Auto' : 'Manual'}</span> : null}
        </summary>
        {item.steps.length ? (
          <ol className="c3-camp-steps">
            {item.steps.map((step, i) => (
              <li key={`${i}-${step}`}>{step}</li>
            ))}
          </ol>
        ) : live && open && member ? null : (
          // In Live the agent below lists the steps it works out, so this line would contradict the panel under it.
          <p className="c3-camp-note">No steps written for this incident yet.</p>
        )}
        {live && open && member ? (
          <MemberAgent
            incident={incidentFromThreatEvent(member.threat.event, member.threat.candidate)}
            onDone={onDone}
          />
        ) : null}
      </details>
    </li>
  );
};

export default CampaignDetail;
